import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

// Broadcast when the visitor has dealt with cookies (accept or dismiss) so
// coordinated popups — the welcome campaign modal — can wait for this before
// showing. Guarantees the order: cookies first, then the campaign popup.
export const CONSENT_RESOLVED_EVENT = 'dialine:cookieConsentResolved';

const CookieConsent = () => {
  const { t } = useLanguage();
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    // Show only if the visitor hasn't already accepted/dismissed.
    if (!Cookies.get('cookieConsent')) {
      setShowConsent(true);
    }
  }, []);

  const resolve = useCallback((value, days) => {
    Cookies.set('cookieConsent', value, { expires: days });
    setShowConsent(false);
    // Tell any waiting popup the cookie step is done.
    window.dispatchEvent(new Event(CONSENT_RESOLVED_EVENT));
  }, []);

  const handleAccept = useCallback(() => resolve('accepted', 365), [resolve]);
  const handleDismiss = useCallback(() => resolve('dismissed', 7), [resolve]);

  // Escape dismisses (parity with the campaign dialog).
  useEffect(() => {
    if (!showConsent) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showConsent, handleDismiss]);

  if (!showConsent) return null;

  const message = t('cookieConsent.message');

  const CloseIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );

  const CookieIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
      <path d="M8.5 8.5v.01" />
      <path d="M16 15.5v.01" />
      <path d="M12 12v.01" />
      <path d="M11 17v.01" />
      <path d="M7 14v.01" />
    </svg>
  );

  // Sand surface, as on the rest of the site.
  const surface = { background: 'linear-gradient(135deg, rgb(var(--brand-tint)) 0%, #F6F3F0 45%, #F6F3F0 100%)' };
  const dots = (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 rounded-[inherit]"
      style={{ backgroundImage: 'radial-gradient(rgba(17,24,39,0.07) 1px, transparent 1px)', backgroundSize: '18px 18px' }}
    />
  );
  const acceptBtn = 'rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_-12px_rgb(var(--brand)/0.6)] transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2';
  const declineBtn = 'rounded-full bg-white px-5 py-3 text-sm font-semibold text-gray-600 ring-1 ring-black/[0.07] transition-colors hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300';
  const privacyLink = (
    <Link to="/privacypolicy" className="font-semibold text-gray-700 underline decoration-black/20 underline-offset-2 transition-colors hover:text-brand">
      {t('footer.links.privacy')}
    </Link>
  );

  return (
    <>
      <style>{`
        @keyframes cookiePop {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) { .cookie-pop { animation: none !important; } }
      `}</style>

      {/* ===== Tablet & desktop: a floating card, bottom-left =====
          Sits above the mobile tab bar on tablets (which is shown below the
          custom breakpoint) and in the page gutter on desktop. Non-modal:
          the page stays usable behind it. */}
      <div
        role="region"
        aria-labelledby="cookie-consent-heading"
        className="cookie-pop fixed left-4 right-4 z-[10000] hidden sm:right-auto sm:block sm:w-[30rem] bottom-[calc(5rem+env(safe-area-inset-bottom))] custom:bottom-6 custom:left-6"
        style={{ animation: 'cookiePop 0.35s cubic-bezier(0.22,1,0.36,1)' }}
      >
        <div
          className="relative rounded-[1.75rem] p-5 ring-1 ring-black/[0.06] shadow-[0_30px_70px_-30px_rgba(17,24,39,0.55)]"
          style={surface}
        >
          {dots}
          <button
            onClick={handleDismiss}
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-400 ring-1 ring-black/[0.06] transition-colors hover:text-gray-700"
            aria-label={t('cookieConsent.dismissAriaLabel')}
          >
            {CloseIcon}
          </button>

          <div className="relative flex items-start gap-4 pr-8">
            <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-black/[0.05] shadow-[0_10px_24px_-14px_rgba(17,24,39,0.5)]">
              {CookieIcon}
            </span>
            <div className="min-w-0">
              <h2 id="cookie-consent-heading" className="text-base font-extrabold tracking-[-0.01em] text-gray-900">
                {t('cookieConsent.title')}
              </h2>
              <p className="mt-1 text-[13.5px] leading-relaxed text-gray-600">
                {message} {privacyLink}
              </p>
            </div>
          </div>

          <div className="relative mt-4 flex items-center justify-end gap-2">
            <button onClick={handleDismiss} className={declineBtn}>
              {t('cookieConsent.decline')}
            </button>
            <button onClick={handleAccept} className={`${acceptBtn} min-w-[120px]`}>
              {t('cookieConsent.accept')}
            </button>
          </div>
        </div>
      </div>

      {/* ===== Mobile: centered popup ===== */}
      <div
        className="sm:hidden fixed inset-0 z-[10001] flex items-end justify-center p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
      >
        {/* Backdrop — tap to dismiss */}
        <div
          className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
          aria-hidden="true"
          onClick={handleDismiss}
        />
        <div
          className="cookie-pop relative w-full max-w-sm rounded-[2rem] p-6 text-center ring-1 ring-black/[0.06] shadow-2xl"
          style={{ ...surface, animation: 'cookiePop 0.3s cubic-bezier(0.22,1,0.36,1)' }}
        >
          {dots}
          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-400 ring-1 ring-black/[0.06] transition-colors hover:text-gray-700"
            aria-label={t('cookieConsent.dismissAriaLabel')}
          >
            {CloseIcon}
          </button>

          <div className="relative">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-black/[0.05] shadow-[0_12px_28px_-14px_rgba(17,24,39,0.5)]">
              {CookieIcon}
            </div>

            <h2 id="cookie-consent-title" className="mb-2 text-xl font-extrabold tracking-[-0.02em] text-gray-900">
              {t('cookieConsent.title')}
            </h2>
            <p className="mb-5 text-sm leading-relaxed text-gray-600">
              {message} {privacyLink}
            </p>

            <div className="flex flex-col gap-2">
              <button onClick={handleAccept} className={`${acceptBtn} w-full active:scale-[0.98]`}>
                {t('cookieConsent.accept')}
              </button>
              <button onClick={handleDismiss} className={`${declineBtn} w-full`}>
                {t('cookieConsent.decline')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
